import {
  useGetList,
  useLocaleState,
  useRecordContext,
  useTranslate,
} from "ra-core";

import { formatLocalizedDate, formatRelativeDate } from "../misc/RelativeDate";
import type { Contact, TemperatureChange } from "../types";
import {
  CorrectTemperatureButton,
  RemoveCorrectionButton,
} from "./CorrectTemperatureButton";
import { RegisterSignalButton } from "./RegisterSignalButton";
import { TemperatureBadge } from "./TemperatureBadge";
import { isTemperature } from "./temperatureModel";

const HISTORY_SIZE = 5;

// [hiperautomacao] The contact's temperature: what it is, why, since when,
// and the two things a seller can do about it.
export const ContactTemperature = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const [locale = "pt-BR"] = useLocaleState();
  if (!record) return null;

  return (
    <div className="flex flex-col gap-3">
      {isTemperature(record.temperature) ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <TemperatureBadge
              temperature={record.temperature}
              isCorrected={record.temperature_corrected}
            />
            {record.temperature_since ? (
              <span className="text-xs text-muted-foreground">
                {translate("hiper.temperature.since", {
                  date: formatRelativeDate(record.temperature_since, locale),
                })}
              </span>
            ) : null}
          </div>
          {record.temperature_reason ? (
            <p className="text-sm">{record.temperature_reason}</p>
          ) : null}
          {record.temperature_corrected &&
          record.temperature_correction_until ? (
            <p className="text-xs text-muted-foreground">
              {translate("hiper.temperature.corrected", {
                date: formatLocalizedDate(
                  record.temperature_correction_until,
                  locale,
                ),
              })}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {translate("hiper.temperature.calculated_hint")}
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("hiper.temperature.none")}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <RegisterSignalButton />
        <CorrectTemperatureButton />
        <RemoveCorrectionButton />
      </div>

      <TemperatureHistory contactId={record.id} locale={locale} />
    </div>
  );
};

interface TemperatureHistoryProps {
  contactId: Contact["id"];
  locale: string;
}

const TemperatureHistory = ({ contactId, locale }: TemperatureHistoryProps) => {
  const translate = useTranslate();
  const { data, isPending } = useGetList<TemperatureChange>(
    "temperature_history",
    {
      pagination: { page: 1, perPage: HISTORY_SIZE },
      sort: { field: "date", order: "DESC" },
      filter: { contact_id: contactId },
    },
  );
  if (isPending) return null;

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-medium text-muted-foreground">
        {translate("hiper.temperature.history")}
      </p>
      {data?.length ? (
        <ul className="flex flex-col gap-1">
          {data.map((change) => (
            <li key={change.id} className="text-xs flex flex-col">
              <span className="flex items-center gap-1 flex-wrap">
                <TemperatureBadge
                  temperature={change.from_temperature}
                  compact
                />
                {change.from_temperature ? (
                  <span aria-hidden="true">→</span>
                ) : null}
                <TemperatureBadge temperature={change.to_temperature} compact />
                <span className="text-muted-foreground">
                  {translate(`hiper.temperature.causes.${change.cause}`, {
                    _: change.cause,
                  })}
                  {" · "}
                  {formatRelativeDate(change.date, locale)}
                </span>
              </span>
              <span className="text-muted-foreground">{change.reason}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">
          {translate("hiper.temperature.history_empty")}
        </p>
      )}
    </div>
  );
};
